import { ReactNode, useMemo } from 'react'
import { useAuth } from '../../auth'
import { QHSE_PERMS } from './permissions'
import { hasAnyMenuTreeCanView } from '../../../shared/utils/menuTree'
import type { MenuTreeNode } from '../../../shared/types/menu-tree'

type AccessFlag = 'canView' | 'canAdd' | 'canEdit' | 'canDelete' | 'canDownload'
type QhseMenuSelector = {
  menuName?: string
  submenuName?: string
  alternateMenuNames?: string[]
}

const normalizeMenuValue = (value?: string | null) => String(value || '').trim().toLowerCase()

const resolveMenuNode = (
  tree: MenuTreeNode[] | undefined,
  selector: QhseMenuSelector | undefined
): MenuTreeNode | undefined => {
  if (!Array.isArray(tree) || tree.length === 0 || !selector?.menuName) return undefined

  const targetMenu = normalizeMenuValue(selector.menuName)
  const targetSubmenu = normalizeMenuValue(selector.submenuName)
  const altMenus = (selector.alternateMenuNames || []).map(normalizeMenuValue)

  const isMenuMatch = (name?: string | null) => {
    const n = normalizeMenuValue(name)
    return n === targetMenu || altMenus.includes(n)
  }

  // 1. Direct top-level match
  for (const node of tree) {
    if (isMenuMatch(node?.name)) {
      if (!targetSubmenu) return node
      const child = (node.children || []).find(
        (item) => normalizeMenuValue(item?.name) === targetSubmenu
      )
      return child ?? node
    }
  }

  // 2. Nested child under a parent group (e.g. certificates under Forms & Checklists)
  for (const node of tree) {
    if (node.children && node.children.length > 0) {
      const child = node.children.find((item) => isMenuMatch(item?.name))
      if (child) {
        if (!targetSubmenu) return child
        const grandChild = (child.children || []).find(
          (item) => normalizeMenuValue(item?.name) === targetSubmenu
        )
        return grandChild ?? child
      }
    }
  }

  return undefined
}

export type QhseRouteAccessKey =
  | 'documentLibrary'
  | 'vesselCertificates'
  | 'officeCertificates'
  | 'manualsAndPlans'
  | 'formsChecklistsSubmissions'
  | 'formsChecklistsLibrary'
  | 'vesselAudits'
  | 'nearMissReports'
  | 'vesselInspection'
  | 'defectsList'
  | 'riskAssessmentLibrary'
  | 'safetyCommitteeMeeting'
  | 'workPermitsAndChecklists'
  | 'monthEndLibrary'

type PermBundle = {
  read: string
  create: string
  update: string
  delete: string
  download: string
}

const QHSE_ROUTE_SELECTORS: Record<QhseRouteAccessKey, QhseMenuSelector> = {
  documentLibrary: { menuName: 'Document Library' },
  vesselCertificates: { menuName: 'Vessel Certificates' },
  officeCertificates: { menuName: 'Office Certificates' },
  manualsAndPlans: { menuName: 'Manuals and Plans' },
  formsChecklistsSubmissions: { menuName: 'Forms & Checklists', submenuName: 'Submissions' },
  formsChecklistsLibrary: { menuName: 'Static Templates', alternateMenuNames: ['Forms & Checklists', 'Library', 'Static Templates'] },
  vesselAudits: { menuName: 'Audits and Inspection', alternateMenuNames: ['Vessel Audits', 'Audit'] },
  nearMissReports: { menuName: 'Near Miss Reports', alternateMenuNames: ['Near Miss', 'Accident Near Miss Reports'] },
  vesselInspection: { menuName: 'Audits and Inspection', alternateMenuNames: ['Vessel Inspection'] },
  defectsList: { menuName: 'Defects List' },
  riskAssessmentLibrary: {
    menuName: 'Risk Assessment Form',
    alternateMenuNames: ['Risk Assessment Library', 'Risk Assessment'],
  },
  safetyCommitteeMeeting: { menuName: 'Safety committee meeting' },
  workPermitsAndChecklists: { menuName: 'Work Permits and Checklists' },
  monthEndLibrary: { menuName: 'Month End Library' },
}

const ROUTE_PERM_BUNDLE: Record<QhseRouteAccessKey, PermBundle> = {
  documentLibrary: {
    read: QHSE_PERMS.DOC_READ,
    create: QHSE_PERMS.DOC_CREATE,
    update: QHSE_PERMS.DOC_UPDATE,
    delete: QHSE_PERMS.DOC_DELETE,
    download: QHSE_PERMS.DOC_DOWNLOAD,
  },
  vesselCertificates: {
    read: QHSE_PERMS.CERT_READ,
    create: QHSE_PERMS.CERT_CREATE,
    update: QHSE_PERMS.CERT_UPDATE,
    delete: QHSE_PERMS.CERT_DELETE,
    download: QHSE_PERMS.CERT_DOWNLOAD,
  },
  officeCertificates: {
    read: QHSE_PERMS.OFFICE_CERT_READ,
    create: QHSE_PERMS.OFFICE_CERT_CREATE,
    update: QHSE_PERMS.OFFICE_CERT_UPDATE,
    delete: QHSE_PERMS.OFFICE_CERT_DELETE,
    download: QHSE_PERMS.OFFICE_CERT_DOWNLOAD,
  },
  manualsAndPlans: {
    read: QHSE_PERMS.MANUAL_PLAN_READ,
    create: QHSE_PERMS.MANUAL_PLAN_CREATE,
    update: QHSE_PERMS.MANUAL_PLAN_UPDATE,
    delete: QHSE_PERMS.MANUAL_PLAN_DELETE,
    download: QHSE_PERMS.MANUAL_PLAN_DOWNLOAD,
  },
  formsChecklistsSubmissions: {
    read: QHSE_PERMS.FORM_SUBMISSION_READ,
    create: QHSE_PERMS.FORM_SUBMISSION_CREATE,
    update: QHSE_PERMS.FORM_SUBMISSION_CREATE,
    delete: QHSE_PERMS.FORM_SUBMISSION_DELETE,
    download: QHSE_PERMS.FORM_SUBMISSION_READ,
  },
  formsChecklistsLibrary: {
    read: QHSE_PERMS.FORM_TEMPLATE_READ,
    create: QHSE_PERMS.FORM_TEMPLATE_CREATE,
    update: QHSE_PERMS.FORM_TEMPLATE_UPDATE,
    delete: QHSE_PERMS.FORM_TEMPLATE_DELETE,
    download: QHSE_PERMS.FORM_TEMPLATE_DOWNLOAD,
  },
  vesselAudits: {
    read: QHSE_PERMS.AUDIT_READ,
    create: QHSE_PERMS.AUDIT_CREATE,
    update: QHSE_PERMS.AUDIT_UPDATE,
    delete: QHSE_PERMS.AUDIT_DELETE,
    download: QHSE_PERMS.AUDIT_READ,
  },
  nearMissReports: {
    read: QHSE_PERMS.NEAR_MISS_READ,
    create: QHSE_PERMS.NEAR_MISS_CREATE,
    update: QHSE_PERMS.NEAR_MISS_UPDATE,
    delete: QHSE_PERMS.NEAR_MISS_DELETE,
    download: QHSE_PERMS.NEAR_MISS_SUPPORT_DOWNLOAD,
  },
  vesselInspection: {
    read: QHSE_PERMS.INSPECTION_READ,
    create: QHSE_PERMS.INSPECTION_CREATE,
    update: QHSE_PERMS.INSPECTION_UPDATE,
    delete: QHSE_PERMS.INSPECTION_DELETE,
    download: QHSE_PERMS.INSPECTION_READ,
  },
  defectsList: {
    read: QHSE_PERMS.DEFECT_READ,
    create: QHSE_PERMS.DEFECT_CREATE,
    update: QHSE_PERMS.DEFECT_UPDATE,
    delete: QHSE_PERMS.DEFECT_DELETE,
    download: QHSE_PERMS.DEFECT_READ,
  },
  riskAssessmentLibrary: {
    read: QHSE_PERMS.RISK_LIB_READ,
    create: QHSE_PERMS.RISK_LIB_TASK_CREATE,
    update: QHSE_PERMS.RISK_LIB_TASK_UPDATE,
    delete: QHSE_PERMS.RISK_LIB_TASK_DELETE,
    download: QHSE_PERMS.RISK_DOWNLOAD,
  },
  safetyCommitteeMeeting: {
    read: QHSE_PERMS.SAFETY_COMMITTEE_READ,
    create: QHSE_PERMS.SAFETY_COMMITTEE_CREATE,
    update: QHSE_PERMS.SAFETY_COMMITTEE_UPDATE,
    delete: QHSE_PERMS.SAFETY_COMMITTEE_DELETE,
    download: QHSE_PERMS.SAFETY_COMMITTEE_READ,
  },
  workPermitsAndChecklists: {
    read: QHSE_PERMS.WORK_PERMIT_READ,
    create: QHSE_PERMS.WORK_PERMIT_CREATE,
    update: QHSE_PERMS.WORK_PERMIT_UPDATE,
    delete: QHSE_PERMS.WORK_PERMIT_DELETE,
    download: QHSE_PERMS.WORK_PERMIT_ATTACH_DOWNLOAD,
  },
  monthEndLibrary: {
    read: QHSE_PERMS.MONTH_END_ME_READ,
    create: QHSE_PERMS.MONTH_END_ME_CREATE,
    update: QHSE_PERMS.MONTH_END_ME_UPDATE,
    delete: QHSE_PERMS.MONTH_END_ME_DELETE,
    download: QHSE_PERMS.MONTH_END_ME_READ,
  },
}

const MODULE_READ_PERM_UNION = Array.from(
  new Set(Object.values(ROUTE_PERM_BUNDLE).map((b) => b.read))
)

const bundleFlagToPerm = (flag: AccessFlag, bundle: PermBundle): string => {
  switch (flag) {
    case 'canView':
      return bundle.read
    case 'canAdd':
      return bundle.create
    case 'canEdit':
      return bundle.update
    case 'canDelete':
      return bundle.delete
    case 'canDownload':
      return bundle.download
    default:
      return bundle.read
  }
}

const resolveBundleForSelector = (selector: QhseMenuSelector | undefined): PermBundle | undefined => {
  if (!selector) return undefined
  const keyBySub = (Object.entries(QHSE_ROUTE_SELECTORS) as [QhseRouteAccessKey, QhseMenuSelector][]).find(
    ([, v]) =>
      String(v.menuName || '').toLowerCase() === String(selector.menuName || '').toLowerCase() &&
      String(v.submenuName || '').toLowerCase() === String(selector.submenuName || '').toLowerCase()
  )
  return keyBySub ? ROUTE_PERM_BUNDLE[keyBySub[0]] : undefined
}

const useQhseSelectorAccess = (selector: QhseMenuSelector | undefined) => {
  const { can, permissionsLoaded } = useAuth()
  const bundle = useMemo(() => resolveBundleForSelector(selector), [selector])

  const resolve = (flag: AccessFlag) => {
    if (!permissionsLoaded || !bundle) return false
    return can(bundleFlagToPerm(flag, bundle))
  }

  return {
    canView: resolve('canView'),
    canAdd: resolve('canAdd'),
    canEdit: resolve('canEdit'),
    canDelete: resolve('canDelete'),
    canDownload: resolve('canDownload'),
  }
}

export const useQhseMenuAccess = (menuName: string, submenuName?: string) => {
  const selector = useMemo<QhseMenuSelector>(
    () => ({ menuName, submenuName }),
    [menuName, submenuName]
  )
  return useQhseSelectorAccess(selector)
}

export const useQhseRouteAccess = (routeKey: QhseRouteAccessKey) => {
  const selector = useMemo<QhseMenuSelector>(() => QHSE_ROUTE_SELECTORS[routeKey], [routeKey])
  return useQhseSelectorAccess(selector)
}

export const useQhseModuleView = () => {
  const { can, permissionsLoaded } = useAuth()

  if (!permissionsLoaded) return false

  return MODULE_READ_PERM_UNION.some((p) => can(p))
}

type RequireQhseMenuProps = {
  menuName: string
  submenuName?: string
  fallback?: ReactNode
  children: ReactNode
}

export const RequireQhseMenuView = ({
  menuName,
  submenuName,
  fallback = null,
  children,
}: RequireQhseMenuProps): JSX.Element | null => {
  const { canView } = useQhseMenuAccess(menuName, submenuName)
  if (!canView) return fallback ? <>{fallback}</> : null
  return <>{children}</>
}

type RequireQhseRouteProps = {
  routeKey: QhseRouteAccessKey
  fallback?: ReactNode
  children: ReactNode
}

export const RequireQhseRouteView = ({
  routeKey,
  fallback = null,
  children,
}: RequireQhseRouteProps): JSX.Element | null => {
  const { canView } = useQhseRouteAccess(routeKey)
  if (!canView) return fallback ? <>{fallback}</> : null
  return <>{children}</>
}
